import { customerService } from './customerService';

export const csvService = {
  // Gerar template CSV para download
  generateTemplate() {
    const headers = [
      'nome',
      'cpf_cnpj', 
      'telefone',
      'email',
      'emails_adicionais',
      'data_nascimento',
      'cep',
      'endereco',
      'numero',
      'complemento',
      'bairro',
      'cidade',
      'estado',
      'codigo_indicacao'
    ];

    const sampleData = [
      '"João Silva"',
      '"12345678901"',
      '"11999999999"',
      '"joao@email.com"',
      '"joao.silva@empresa.com;joao.pessoal@gmail.com"',
      '"1990-01-15"',
      '"01234567"',
      '"Rua das Flores"',
      '"123"',
      '"Apt 45"',
      '"Centro"',
      '"São Paulo"',
      '"SP"',
      '"ABC1234"'
    ];

    const instructions = [
      '"INSTRUÇÕES:"',
      '"- Mantenha o cabeçalho (primeira linha) inalterado"',
      '"- Nome, CPF/CNPJ e Telefone são obrigatórios"',
      '"- Data no formato: AAAA-MM-DD (ex: 1990-01-15)"',
      '"- CEP apenas números (ex: 01234567)"',
      '"- CPF apenas números (ex: 12345678901)"',
      '"- CNPJ apenas números (ex: 12345678000195)"',
      '"- Telefone apenas números (ex: 11999999999)"',
      '"- E-mail principal obrigatório"',
      '"- E-mails adicionais separados por ; (ex: email1@teste.com;email2@teste.com)"',
      '"- Código de indicação opcional"',
      '"- Apague estas linhas de instrução antes de importar"',
      '""',
      '""',
      '"EXEMPLO DE DADOS:"'
    ];

    const csvContent = [
      headers.join(','),
      ...instructions.map(line => line),
      sampleData.join(',')
    ].join('\n');

    return csvContent;
  },

  // Download do template
  downloadTemplate() {
    const csvContent = this.generateTemplate();
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', 'modelo_importacao_clientes.csv');
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  },

  // Validar formato do CSV
  validateCsvStructure(csvText) {
    const lines = csvText.trim().split('\n');
    
    if (lines.length < 2) {
      throw new Error('O arquivo deve conter pelo menos o cabeçalho e uma linha de dados');
    }

    const headers = this.parseCSVLine(lines[0]).map(h => h.trim().toLowerCase());
    const requiredHeaders = ['nome', 'telefone'];
    
    // Verificar se tem CPF ou CPF_CNPJ
    const hasCpfOrCnpj = headers.includes('cpf') || headers.includes('cpf_cnpj');
    if (!hasCpfOrCnpj) {
      requiredHeaders.push('cpf_cnpj');
    }
    
    const missingHeaders = requiredHeaders.filter(header => 
      !headers.includes(header)
    );

    if (missingHeaders.length > 0) {
      throw new Error(`Colunas obrigatórias ausentes: ${missingHeaders.join(', ')}`);
    }

    // Filtrar linhas de dados válidas (ignorar instruções e linhas vazias)
    const dataLines = lines.slice(1).filter(line => {
      const trimmedLine = line.trim();
      if (!trimmedLine) return false;
      
      // Ignorar linhas que começam com INSTRUÇÕES, EXEMPLO, ou são vazias
      const firstCol = this.parseCSVLine(trimmedLine)[0] || '';
      return !firstCol.toLowerCase().includes('instruções') && 
             !firstCol.toLowerCase().includes('exemplo') &&
             trimmedLine !== '""' &&
             trimmedLine !== '';
    });

    if (dataLines.length === 0) {
      throw new Error('Nenhuma linha de dados válida encontrada no arquivo');
    }

    return { headers, dataLines };
  },

  // Processar linha do CSV com melhor parsing
  processLine(line, headers) {
    const values = this.parseCSVLine(line);
    
    const customerData = {};
    headers.forEach((header, index) => {
      const value = (values[index] || '').trim();
      
      switch (header) {
        case 'nome':
          customerData.name = value;
          break;
        case 'cpf':
        case 'cpf_cnpj':
          customerData.cpf = value.replace(/[^\d]/g, '');
          break;
        case 'telefone':
          customerData.phone = value.replace(/[^\d]/g, '');
          break;
        case 'email':
          customerData.email = value.toLowerCase().trim();
          break;
        case 'emails_adicionais':
          // Processar e-mails adicionais separados por ;
          if (value && value.trim()) {
            const additionalEmails = value.split(';')
              .map(email => email.toLowerCase().trim())
              .filter(email => email); // Remove e-mails vazios
            
            // Combinar e-mail principal com adicionais
            const allEmails = [customerData.email];
            additionalEmails.forEach(email => {
              if (!allEmails.includes(email)) { // Evitar duplicatas
                allEmails.push(email);
              }
            });
            
            customerData.emails = allEmails.filter(email => email); // Remove vazios
          }
          break;
        case 'data_nascimento':
          customerData.birthDate = value;
          break;
        case 'cep':
          customerData.cep = value.replace(/[^\d]/g, '');
          break;
        case 'endereco':
          customerData.street = value;
          break;
        case 'numero':
          customerData.number = value;
          break;
        case 'complemento':
          customerData.complement = value;
          break;
        case 'bairro':
          customerData.neighborhood = value;
          break;
        case 'cidade':
          customerData.city = value;
          break;
        case 'estado':
          customerData.state = value;
          break;
        case 'codigo_indicacao':
          customerData.referralCode = value;
          break;
      }
    });

    // Garantir que sempre tenha um array de e-mails, mesmo que seja só com o principal
    if (!customerData.emails && customerData.email) {
      customerData.emails = [customerData.email];
    } else if (customerData.emails && !customerData.emails.includes(customerData.email)) {
      // Se o e-mail principal não estiver na lista, adicioná-lo no início
      customerData.emails.unshift(customerData.email);
    }

    return customerData;
  },

  // Parser CSV mais robusto que lida com vírgulas dentro de aspas
  parseCSVLine(line) {
    const result = [];
    let current = '';
    let inQuotes = false;
    
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        result.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    
    result.push(current.trim());
    return result.map(value => value.replace(/^"|"$/g, ''));
  },

  // Validar dados do cliente
  validateCustomerData(customerData, lineNumber) {
    const errors = [];

    if (!customerData.name || customerData.name.trim() === '') {
      errors.push(`Linha ${lineNumber}: Nome é obrigatório`);
    }

    if (!customerData.cpf || (customerData.cpf.length !== 11 && customerData.cpf.length !== 14)) {
      errors.push(`Linha ${lineNumber}: CPF deve ter 11 dígitos ou CNPJ deve ter 14 dígitos`);
    }

    if (!customerData.phone || customerData.phone.length < 10) {
      errors.push(`Linha ${lineNumber}: Telefone deve ter pelo menos 10 dígitos`);
    }

    if (customerData.email && !customerData.email.includes('@')) {
      errors.push(`Linha ${lineNumber}: Email inválido`);
    }

    if (customerData.birthDate && !this.isValidDate(customerData.birthDate)) {
      errors.push(`Linha ${lineNumber}: Data de nascimento inválida (use formato AAAA-MM-DD)`);
    }

    return errors;
  },

  // Validar data
  isValidDate(dateString) {
    const date = new Date(dateString);
    return date instanceof Date && !isNaN(date) && dateString.match(/^\d{4}-\d{2}-\d{2}$/);
  },

  // Processar importação completa
  async processImport(file, companyId) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = async (e) => {
        try {
          const csvText = e.target.result;
          const { headers, dataLines } = this.validateCsvStructure(csvText);
          
          const results = {
            total: dataLines.length,
            processed: 0,
            created: 0,
            updated: 0,
            errors: []
          };

          // Processar cada linha
          for (let i = 0; i < dataLines.length; i++) {
            const line = dataLines[i].trim();
            if (!line) continue; // Pular linhas vazias

            const lineNumber = i + 2; // +2 porque começamos do índice 0 e tem o cabeçalho
            
            try {
              const customerData = this.processLine(line, headers);
              
              // Validar dados
              const validationErrors = this.validateCustomerData(customerData, lineNumber);
              if (validationErrors.length > 0) {
                results.errors.push(...validationErrors);
                continue;
              }

              // Adicionar companyId
              customerData.companyId = companyId;

              // Tentar UPSERT
              const upsertResult = await this.upsertCustomer(customerData);
              
              if (upsertResult.created) {
                results.created++;
              } else if (upsertResult.updated) {
                results.updated++;
              }
              
              results.processed++;
              
            } catch (error) {
              results.errors.push(`Linha ${lineNumber}: ${error.message}`);
            }
          }

          resolve(results);
          
        } catch (error) {
          reject(error);
        }
      };

      reader.onerror = () => {
        reject(new Error('Erro ao ler o arquivo'));
      };

      reader.readAsText(file, 'UTF-8');
    });
  },

  // Operação UPSERT (Update ou Insert)
  async upsertCustomer(customerData) {
    try {
      // Verificar se cliente já existe pelo CPF/CNPJ na empresa
      const existingCustomers = await customerService.getByDocument(customerData.cpf);
      const existingCustomer = existingCustomers.find(c => c.companyId === customerData.companyId);

      if (existingCustomer) {
        // Cliente existe - UPDATE
        await customerService.update(existingCustomer.id, customerData);
        return { created: false, updated: true, customerId: existingCustomer.id };
      } else {
        // Cliente não existe - CREATE
        const newCustomer = await customerService.create(customerData);
        return { created: true, updated: false, customerId: newCustomer.id };
      }
    } catch (error) {
      throw new Error(`Erro no UPSERT: ${error.message}`);
    }
  }
};
